import { z } from 'zod';
import { isReservedEmailDomain } from './email-address';

/**
 * The contact form contract, shared by the browser form and the Worker. The
 * Worker is the authority; the browser validates only to show friendly
 * messages sooner.
 *
 * Error messages are stable codes, not sentences: the Worker answers in
 * whichever language the visitor was reading, and the browser looks the code
 * up in its own translations (`contact.errors.<code>` in the i18n files).
 *
 * `website` is a honeypot. The visible form hides it from people and from
 * assistive technology, so a value there means a bot. The Worker answers
 * exactly as it would a real submission and sends nothing.
 */
export const contactErrorCodes = [
  'name_required',
  'name_too_long',
  'email_invalid',
  'email_unreachable',
  'organization_too_long',
  'message_required',
  'message_too_long',
  'invalid_request',
  'rate_limited',
  'not_configured',
  'send_failed',
] as const;
export type ContactErrorCode = (typeof contactErrorCodes)[number];

export const CONTACT_LIMITS = { name: 200, email: 320, organization: 200, message: 5000 } as const;
/** The honeypot field's limit. */
export const HONEYPOT_LIMIT = 500;

/**
 * The largest request body a valid submission can need, in bytes, so the
 * Worker's streaming body cap never rejects valid input.
 *
 * Field limits count UTF-16 code units. One unit is at most 3 UTF-8 bytes
 * (a 4-byte character is 2 units), and percent-encoding a byte in a form
 * post takes 3 characters: 9 bytes per unit. JSON is smaller (at most 6
 * bytes per unit, for `\u0000`-style escapes). On top: every field name,
 * `=`, `&`, quotes and braces, the two-letter `locale` and `lang` values, and
 * a margin so a message a little over its limit gets the friendly
 * "too long" error rather than a bare 413. 5,000 CJK characters (about 45 KB
 * form-encoded) fit.
 */
const WORST_CASE_BYTES_PER_UNIT = 9;
const FIELD_OVERHEAD_BYTES = 512;
const MARGIN_BYTES = 8 * 1024;
export const CONTACT_BODY_LIMIT =
  (Object.values(CONTACT_LIMITS).reduce((sum, limit) => sum + limit, 0) + HONEYPOT_LIMIT + 4) *
    WORST_CASE_BYTES_PER_UNIT +
  FIELD_OVERHEAD_BYTES +
  MARGIN_BYTES;

export const contactSubmissionSchema = z.object({
  name: z.string({ error: 'name_required' }).trim().min(1, 'name_required').max(CONTACT_LIMITS.name, 'name_too_long'),
  email: z
    .string({ error: 'email_invalid' })
    .trim()
    .max(CONTACT_LIMITS.email, 'email_invalid')
    .pipe(z.email('email_invalid'))
    .refine((value) => !isReservedEmailDomain(value), 'email_unreachable'),
  organization: z.string().trim().max(CONTACT_LIMITS.organization, 'organization_too_long').optional().default(''),
  message: z
    .string({ error: 'message_required' })
    .trim()
    .min(1, 'message_required')
    .max(CONTACT_LIMITS.message, 'message_too_long'),
  /** The language the visitor was reading, so the answer comes back in it. */
  locale: z.enum(['en', 'es']).optional(),
  website: z.string().max(HONEYPOT_LIMIT).optional().default(''),
});

export type ContactSubmission = z.infer<typeof contactSubmissionSchema>;

/** The first problem with a submission, as a code the UI can translate. */
export function firstContactError(error: z.ZodError): ContactErrorCode {
  const message = error.issues[0]?.message;
  return (contactErrorCodes as readonly string[]).includes(message ?? '')
    ? (message as ContactErrorCode)
    : 'invalid_request';
}

/** What GET /api/contact answers, so the page knows whether to show the form. */
export interface ContactStatus {
  enabled: boolean;
}

/** What POST /api/contact answers to a JSON request. */
export type ContactResponse = { ok: true } | { ok: false; error: ContactErrorCode };
